// sharedWallMovement.ts - Utility for moving shared walls together

interface Point {
  x: number;
  z: number;
}

interface Room {
  id: string;
  room_type: string;
  area: number;
  height: number;
  width: number;
  floor_polygon: Point[];
}

/**
 * When moving an edge of a room, find which other rooms share that edge
 * and update their corresponding edge vertices as well
 */
export function moveSharedWall(
  primaryRoomId: string,
  edgeIndices: [number, number],
  delta: { x: number; z: number },
  rooms: Room[],
  calculateRoomDimensions?: (polygon: Point[]) => { width: number; height: number },
  calculateRoomArea?: (polygon: Point[]) => number
): Room[] {
  const primaryRoom = rooms.find((r) => r.id === primaryRoomId);
  if (!primaryRoom) return rooms;

  const [idx1, idx2] = edgeIndices;
  const primaryEdgeStart = primaryRoom.floor_polygon[idx1];
  const primaryEdgeEnd = primaryRoom.floor_polygon[idx2];

  const updatedRooms = rooms.map((room) => {
    // Skip the primary room (it's already being updated elsewhere)
    if (room.id === primaryRoomId) return room;
    if (room.room_type === "Wall" || room.room_type === "Boundary") return room;

    // Check if this room shares the edge
    const sharedEdgeInfo = findSharedEdge(
      primaryEdgeStart,
      primaryEdgeEnd,
      room.floor_polygon
    );

    if (sharedEdgeInfo) {
      const { idx1: otherIdx1, idx2: otherIdx2 } = sharedEdgeInfo;
      
      // Update the shared vertices
      const updatedPolygon = [...room.floor_polygon];
      updatedPolygon[otherIdx1] = {
        x: updatedPolygon[otherIdx1].x + delta.x,
        z: updatedPolygon[otherIdx1].z + delta.z,
      };
      updatedPolygon[otherIdx2] = {
        x: updatedPolygon[otherIdx2].x + delta.x,
        z: updatedPolygon[otherIdx2].z + delta.z,
      };

      // Recalculate dimensions if functions provided
      const updatedRoom = { ...room, floor_polygon: updatedPolygon };
      
      if (calculateRoomDimensions && calculateRoomArea) {
        const dimensions = calculateRoomDimensions(updatedPolygon);
        const area = calculateRoomArea(updatedPolygon);
        updatedRoom.width = dimensions.width;
        updatedRoom.height = dimensions.height;
        updatedRoom.area = area;
      }

      return updatedRoom;
    }

    return room;
  });

  return updatedRooms;
}

/**
 * Find if a polygon has an edge that matches the given edge
 * Returns the indices if found, null otherwise
 */
function findSharedEdge(
  edgeStart: Point,
  edgeEnd: Point,
  polygon: Point[],
  tolerance = 2.0
): { idx1: number; idx2: number } | null {
  for (let i = 0; i < polygon.length; i++) {
    const nextIdx = (i + 1) % polygon.length;
    const p1 = polygon[i];
    const p2 = polygon[nextIdx];

    // Check if this edge matches (forward or reverse direction)
    const distStartToP1 = Math.sqrt(
      Math.pow(edgeStart.x - p1.x, 2) + Math.pow(edgeStart.z - p1.z, 2)
    );
    const distEndToP2 = Math.sqrt(
      Math.pow(edgeEnd.x - p2.x, 2) + Math.pow(edgeEnd.z - p2.z, 2)
    );

    // Forward match
    if (distStartToP1 < tolerance && distEndToP2 < tolerance) {
      return { idx1: i, idx2: nextIdx };
    }

    // Reverse match
    const distStartToP2 = Math.sqrt(
      Math.pow(edgeStart.x - p2.x, 2) + Math.pow(edgeStart.z - p2.z, 2)
    );
    const distEndToP1 = Math.sqrt(
      Math.pow(edgeEnd.x - p1.x, 2) + Math.pow(edgeEnd.z - p1.z, 2)
    );

    if (distStartToP2 < tolerance && distEndToP1 < tolerance) {
      return { idx1: nextIdx, idx2: i };
    }
  }

  return null;
}

/**
 * When moving a vertex of a room, find which other rooms share that vertex
 * and update their corresponding vertex as well
 */
export function moveSharedVertex(
  primaryRoomId: string,
  vertexIndex: number,
  delta: { x: number; z: number },
  rooms: Room[],
  calculateRoomDimensions?: (polygon: Point[]) => { width: number; height: number },
  calculateRoomArea?: (polygon: Point[]) => number
): Room[] {
  const primaryRoom = rooms.find((r) => r.id === primaryRoomId);
  if (!primaryRoom) return rooms;

  const primaryVertex = primaryRoom.floor_polygon[vertexIndex];

  const updatedRooms = rooms.map((room) => {
    // Skip the primary room
    if (room.id === primaryRoomId) return room;
    if (room.room_type === "Wall" || room.room_type === "Boundary") return room;

    // Check if this room shares the vertex
    const sharedVertexIdx = findSharedVertex(primaryVertex, room.floor_polygon);

    if (sharedVertexIdx !== null) {
      const updatedPolygon = [...room.floor_polygon];
      updatedPolygon[sharedVertexIdx] = {
        x: updatedPolygon[sharedVertexIdx].x + delta.x,
        z: updatedPolygon[sharedVertexIdx].z + delta.z,
      };

      // Recalculate dimensions if functions provided
      const updatedRoom = { ...room, floor_polygon: updatedPolygon };
      
      if (calculateRoomDimensions && calculateRoomArea) {
        const dimensions = calculateRoomDimensions(updatedPolygon);
        const area = calculateRoomArea(updatedPolygon);
        updatedRoom.width = dimensions.width;
        updatedRoom.height = dimensions.height;
        updatedRoom.area = area;
      }

      return updatedRoom;
    }

    return room;
  });

  return updatedRooms;
}

/**
 * Find if a polygon has a vertex that matches the given vertex
 */
function findSharedVertex(
  vertex: Point,
  polygon: Point[],
  tolerance = 2.0
): number | null {
  for (let i = 0; i < polygon.length; i++) {
    const dist = Math.sqrt(
      Math.pow(vertex.x - polygon[i].x, 2) + 
      Math.pow(vertex.z - polygon[i].z, 2)
    );

    if (dist < tolerance) {
      return i;
    }
  }

  return null;
}